import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import BrandHeader from "@/components/BrandHeader";
import LandingPage from "@/pages/LandingPage";
import PortalChooserPage from "@/pages/PortalChooserPage";
import AthletesPage from "@/pages/AthletesPage";
import AthleteDetailPage from "@/pages/AthleteDetailPage";
import TestEditorPage from "@/pages/TestEditorPage";
import ReportPreviewPage from "@/pages/ReportPreviewPage";
import CompareTestsPage from "@/pages/CompareTestsPage";
import CompareReportPage from "@/pages/CompareReportPage";
import LoginPage from "@/pages/LoginPage";
import CoachingAthletesPage from "@/pages/coaching/CoachingAthletesPage";
import CoachingAthleteDetailPage from "@/pages/coaching/CoachingAthleteDetailPage";
import CoachMessagesPage from "@/pages/coaching/CoachMessagesPage";
import CoachConversationPage from "@/pages/coaching/CoachConversationPage";
import CoachActivityPage from "@/pages/coaching/CoachActivityPage";
import CoachNav from "@/components/CoachNav";
import CoachPushAutoSync from "@/components/CoachPushAutoSync";
import ReportCardComposerPage from "@/pages/coaching/ReportCardComposerPage";
import CoachingCalendarPage from "@/pages/coaching/CoachingCalendarPage";
import CoachingLibraryPage from "@/pages/coaching/CoachingLibraryPage";
import ProgramEditorPage from "@/pages/coaching/ProgramEditorPage";
import ProgramPlannerPage from "@/pages/coaching/ProgramPlannerPage";
import ProgramWeekReportPage from "@/pages/coaching/ProgramWeekReportPage";
import ProgramFullReportPage from "@/pages/coaching/ProgramFullReportPage";
import AthleteLoginPage from "@/pages/athlete/AthleteLoginPage";
import AthleteSetupPage from "@/pages/athlete/AthleteSetupPage";
import AthleteHomePage from "@/pages/athlete/AthleteHomePage";
import AthleteProgramPage from "@/pages/athlete/AthleteProgramPage";
import AthleteMessagesPage from "@/pages/athlete/AthleteMessagesPage";
import AthleteProgressPage from "@/pages/athlete/AthleteProgressPage";
import StravaConnectLandingPage from "@/pages/athlete/StravaConnectLandingPage";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AthleteAuthProvider } from "@/lib/athleteAuth";
import FontSizeControl from "@/components/FontSizeControl";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--tir-muted)] text-sm">
        Loading…
      </div>
    );
  }
  if (!user) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--tir-muted)] text-sm">
        Loading…
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return <LoginPage />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  const onLoginRoute = loc.pathname === "/login";
  const onAthleteRoute = loc.pathname.startsWith("/athlete");
  const onCoachPreview = /^\/coaching\/programs\/[^/]+\/preview$/.test(loc.pathname);
  const onPortalChooser = loc.pathname === "/";
  return (
    <>
      {user && !loading && !onLoginRoute && !onAthleteRoute && !onCoachPreview && !onPortalChooser && <BrandHeader />}
      <Routes>
        <Route path="/" element={<PortalChooserPage />} />
        <Route path="/login" element={<LoginRoute />} />

        {/* Athlete portal */}
        <Route path="/athlete/login" element={<AthleteLoginPage />} />
        <Route path="/athlete/setup" element={<AthleteSetupPage />} />
        <Route path="/athlete/home" element={<AthleteHomePage />} />
        <Route path="/athlete/programs/:id" element={<AthleteProgramPage />} />
        <Route path="/athlete/messages" element={<AthleteMessagesPage />} />
        <Route path="/athlete/progress" element={<AthleteProgressPage />} />
        <Route path="/athlete/strava-connect" element={<StravaConnectLandingPage />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <LandingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lactate"
          element={
            <ProtectedRoute>
              <AthletesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/athletes/:id"
          element={
            <ProtectedRoute>
              <AthleteDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/athletes/:id/compare"
          element={
            <ProtectedRoute>
              <CompareTestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/athletes/:id/compare/report"
          element={
            <ProtectedRoute>
              <CompareReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tests/:id/edit"
          element={
            <ProtectedRoute>
              <TestEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tests/:id/report"
          element={
            <ProtectedRoute>
              <ReportPreviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching"
          element={
            <ProtectedRoute>
              <CoachingAthletesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/library"
          element={
            <ProtectedRoute>
              <CoachingLibraryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/athletes/:id"
          element={
            <ProtectedRoute>
              <CoachingAthleteDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/messages"
          element={
            <ProtectedRoute>
              <CoachMessagesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/activity"
          element={
            <ProtectedRoute>
              <CoachActivityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/messages/:athleteId"
          element={
            <ProtectedRoute>
              <CoachConversationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/athletes/:id/report-card"
          element={
            <ProtectedRoute>
              <ReportCardComposerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/athletes/:id/calendar"
          element={
            <ProtectedRoute>
              <CoachingCalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/programs/:id/edit"
          element={
            <ProtectedRoute>
              <ProgramEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/programs/:id/planner"
          element={
            <ProtectedRoute>
              <ProgramPlannerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/programs/:id/weeks/:weekNumber/report"
          element={
            <ProtectedRoute>
              <ProgramWeekReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/programs/:id/report"
          element={
            <ProtectedRoute>
              <ProgramFullReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coaching/programs/:id/preview"
          element={
            <ProtectedRoute>
              <AthleteProgramPage coachPreview />
            </ProtectedRoute>
          }
        />
      </Routes>
      {/* Mobile "app look" bottom nav for the coach — same visibility rules
          as BrandHeader, plus hidden on report pages (they print). */}
      {user && !loading && !onLoginRoute && !onAthleteRoute && !onCoachPreview && !onPortalChooser &&
        !loc.pathname.includes("/report") && (
          <>
            <CoachPushAutoSync />
            <CoachNav />
          </>
        )}
    </>
  );
}

function App() {
  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <AthleteAuthProvider>
            <AppRoutes />
            <FontSizeControl />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  borderRadius: 0,
                  border: "1px solid #1C1917",
                  fontFamily: "Montserrat",
                },
              }}
            />
          </AthleteAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
